import dayjs, { Dayjs } from 'dayjs';

/**
 * TmsWaypointTimeUtils工具类
 * (用于根据route配置的行驶时间、停留时间，来计算到达每个waypoint的到达时间和出发时间，以及计算终点的到达时间)
 */

/**
 * 将起点时区的时间转换为目标时区显示
 * @param time 起点时区的时间
 * @param baseOffset 起点时区UTC偏移量（小时）
 * @param targetOffset 目标时区UTC偏移量（小时）
 * @returns 目标时区的时间
 */
export const convertToTargetTimezone = (
    time: Dayjs,
    baseOffset: number,
    targetOffset: number
): Dayjs => {
    if (baseOffset === targetOffset) {
        return time.clone().utcOffset(targetOffset, true);
    }

    // 计算时区偏移差
    const offsetDiff = targetOffset - baseOffset;

    // 转换为目标时区显示
    return time.clone().add(offsetDiff, 'hour').utcOffset(targetOffset, true);
};

/**
 * 时间显示对象
 */
export interface TimeDisplay {
    time: string; // 时间部分，如 "10:00 +1 day"
    timezone: string | undefined; // 时区部分，如 "PT"
}

/**
 * 格式化时间，如果跨越日期则显示天数，并加上时区信息
 * @param time 起点时区的时间
 * @param baseOffset 起点时区UTC偏移量（小时）
 * @param baseDate 起点时区的基准日期
 * @param targetOffset 目标时区UTC偏移量（小时）
 * @param displayTimezone 用于展示的时区缩写（如 "PT"、"ET"）
 * @returns 格式化后的时间显示对象，包含时间部分和时区部分
 */
export const formatTimeWithDayOffsetAndTimezone = (
    time: Dayjs,
    baseOffset: number,
    baseDate: Dayjs,
    targetOffset: number,
    displayTimezone: string | undefined
): TimeDisplay => {
    // 将起点时区的时间转换为目标时区显示
    const displayTime = convertToTargetTimezone(time, baseOffset, targetOffset);
    const timeStr = displayTime.format('HH:mm');

    // 计算日期偏移量：
    // 1. 起点的出发日期（起点时区的本地日期）
    const originDate = baseDate.format('YYYY-MM-DD');

    // 2. 终点的到达日期（终点时区的本地日期）
    const destinationDate = displayTime.startOf('day').format('YYYY-MM-DD');

    // 3. 计算日期差：终点日期 - 起点日期
    const originDateObj = dayjs(originDate);
    const destinationDateObj = dayjs(destinationDate);
    const dayDiff = destinationDateObj.diff(originDateObj, 'day');

    // 构建时间部分字符串
    let timePart = timeStr;
    if (dayDiff === 1) {
        timePart = `${timeStr} +1 day`;
    } else if (dayDiff > 1) {
        timePart = `${timeStr} +${dayDiff} days`;
    } else if (dayDiff === -1) {
        timePart = `${timeStr} -1 day`;
    } else if (dayDiff < -1) {
        timePart = `${timeStr} ${dayDiff} days`;
    }

    return {
        time: timePart,
        timezone: displayTimezone
    };
};

/**
 * Waypoint时间计算结果
 */
export interface WaypointTimeResult {
    arrivalTime: Dayjs; // 起点时区的到达时间
    departureTime: Dayjs; // 起点时区的出发时间
}

/**
 * 计算waypoint的到达时间和出发时间（所有计算都在起点时区进行）
 * @param currentTime 当前时间（起点时区）
 * @param travelTime 行驶时间（小时）
 * @param dwellTime 停留时间（小时）
 * @returns waypoint的到达时间和出发时间（都在起点时区）
 */
export const calculateWaypointTimes = (
    currentTime: Dayjs,
    travelTime: number,
    dwellTime: number
): WaypointTimeResult => {
    // 所有计算都在起点时区进行
    const arrivalTime = currentTime.clone().add(travelTime, 'hour');
    const departureTime = arrivalTime.clone().add(dwellTime, 'hour');

    return {
        arrivalTime,
        departureTime
    };
};

/**
 * 计算终点到达时间（所有计算都在起点时区进行）
 * @param currentTime 当前时间（起点时区）
 * @param travelTime 行驶时间（小时）
 * @returns 终点到达时间（起点时区）
 */
export const calculateDestinationArrivalTime = (
    currentTime: Dayjs,
    travelTime: number
): Dayjs => {
    // 所有计算都在起点时区进行
    return currentTime.clone().add(travelTime, 'hour');
};

/**
 * 初始化起点时间
 * @param departureTimeStr 出发时间字符串（HH:mm格式）
 * @param timezoneOffset 起点时区UTC偏移量（小时）
 * @returns 起点时区的dayjs对象
 */
export const initializeOriginTime = (
    departureTimeStr: string,
    timezoneOffset: number
): Dayjs => {
    const departureTime = dayjs(departureTimeStr, 'HH:mm');
    return departureTime.utcOffset(timezoneOffset, true);
};
